import { useState, useEffect, useRef, memo, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../templates/AuthGate";
import { doLogout } from "../../../model/services/AuthService";
import { i18n } from "../../../translate/i18n";
import { getNavigationOptionsByTenanat } from "../../../viewModel/strategy/menu/NavigationOptions/navigationOptions.strategy";
import { SETTINGS_ROUTES } from "../../../viewModel/useSettingsViewModel";
import { ROUTES_APPLICATION_PATH } from "../../../routes.constants";

import styled, { css } from "styled-components";

const Chevron = styled.span`
  margin-left: auto;
  display: flex;
  align-items: center;
  transition: transform 0.25s ease;
  transform: ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0deg)")};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
`;

const FlyoutBody = styled.div`
  overflow: hidden;
  max-height: ${({ $open }) => ($open ? "1400px" : "0")};
  transition: max-height 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  width: calc(100% - 26px);
  margin: 0 6px 0 20px;
  padding-left: 0.6rem;
  border-left: 2px solid rgba(255, 255, 255, 0.3);
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin: 0.6rem 0.9rem 0.2rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.65);
  white-space: nowrap;

  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background-color: rgba(255, 255, 255, 0.3);
  }
`;

const FlyoutItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.9rem;
  color: #fff;
  font-size: 0.875rem;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  border-radius: 8px;
  white-space: nowrap;
  background-color: transparent;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    background-color: ${({ $disabled }) => ($disabled ? "transparent" : "rgba(255, 255, 255, 0.15)")};
  }

  /* Sub-aba selecionada: overlay translúcido (mais claro), diferente do branco sólido do item de topo. */
  ${({ $active }) =>
    $active &&
    css`
      background-color: rgba(255, 255, 255, 0.22);

      &:hover {
        background-color: rgba(255, 255, 255, 0.22);
      }
    `}
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $expanded }) => ($expanded ? "flex-start" : "center")};
  gap: 0.75rem;
  padding: 0.65rem 0.9rem;
  cursor: pointer;
  width: ${({ $expanded }) => ($expanded ? "calc(100% - 12px)" : "44px")};
  overflow: hidden;
  margin: 0 6px;
  border-radius: 16px;
  color: #fff;
  transition: background-color 0.25s ease, color 0.25s ease, width 0.25s ease;
  white-space: nowrap;
  position: relative;

  ${({ $active }) =>
    $active &&
    css`
      background-color: rgba(255, 255, 255, 0.15);
    `}

  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }

  svg {
    flex-shrink: 0;
  }

  /* Aba selecionada (aberta ou com uma sub-aba ativa): inverte as cores, igual ao destaque de aba ativa. */
  ${({ $selected }) =>
    $selected &&
    css`
      background-color: #fff;
      color: #ED701D;

      &:hover {
        background-color: #fff;
      }
    `}
`;

const StaticItem = styled(MenuItem)`
  cursor: default;
  opacity: 0.85;

  &:hover {
    background-color: transparent;
  }
`;

const MenuLabel = styled.span`
  font-weight: 600;
  font-size: 0.95rem;
  opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
  transition: opacity 0.2s ease;
  color: inherit;
`;

const UserSection = styled.div`
  padding: ${({ $expanded }) => ($expanded ? "0.6rem 1rem" : "0.6rem 0")};
  display: flex;
  align-items: center;
  justify-content: ${({ $expanded }) => ($expanded ? "flex-start" : "center")};
  gap: ${({ $expanded }) => ($expanded ? "0.7rem" : "0")};
  width: 100%;
  flex-shrink: 0;
  color: #fff;

  & > div:last-child {
    opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
    max-width: ${({ $expanded }) => ($expanded ? "200px" : "0")};
    overflow: hidden;
    transition: opacity 0.3s ease, max-width 0.25s ease;
    white-space: nowrap;
    strong { font-size: 0.9rem; display: block; color: #fff; }
    p { margin: 0; font-size: 0.75rem; color: #fff; }
  }
`;

const MENU_ICONS = {
  home: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 11l9-8 9 8"/>
      <path d="M5 10v10a1 1 0 001 1h4v-6h4v6h4a1 1 0 001-1V10"/>
    </svg>
  ),
  plano: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="8" height="4" x="8" y="2" rx="1" ry="1"/>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
      <path d="M12 11h4"/>
      <path d="M12 16h4"/>
      <path d="M8 11h.01"/>
      <path d="M8 16h.01"/>
    </svg>
  ),
  produto: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"/>
      <path d="M12 22V12"/>
      <polyline points="3.29 7 12 12 20.71 7"/>
      <path d="m7.5 4.27 9 5.15"/>
    </svg>
  ),
  abastecimento: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/>
      <path d="M15 18H9"/>
      <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/>
      <circle cx="17" cy="18" r="2"/>
      <circle cx="7" cy="18" r="2"/>
    </svg>
  ),
  canais: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 21v-5a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v5"/>
      <path d="M17.774 10.31a1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.451 0 1.12 1.12 0 0 0-1.548 0 2.5 2.5 0 0 1-3.452 0 1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.77-3.248l2.889-4.184A2 2 0 0 1 7 2h10a2 2 0 0 1 1.653.873l2.895 4.192a2.5 2.5 0 0 1-3.774 3.244"/>
      <path d="M4 10.95V19a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8.05"/>
    </svg>
  ),
  sortimento: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="16" y="16" width="6" height="6" rx="1"/>
      <rect x="2" y="16" width="6" height="6" rx="1"/>
      <rect x="9" y="2" width="6" height="6" rx="1"/>
      <path d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3"/>
      <path d="M12 12V8"/>
    </svg>
  ),
  lightbulb: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18h6"/>
      <path d="M10 22h4"/>
      <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2.05V17h6v-.25c0-.85.4-1.55 1-2.05A7 7 0 0 0 12 2z"/>
    </svg>
  ),
  sitemap: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="6" r="2"/>
      <circle cx="18" cy="6" r="2"/>
      <circle cx="12" cy="18" r="2"/>
      <path d="M6 8v4a2 2 0 0 0 2 2h1"/>
      <path d="M18 8v4a2 2 0 0 0-2 2h-1"/>
      <path d="M12 14v2"/>
    </svg>
  ),
  storeSmall: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 21v-5a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v5"/>
      <path d="M17.774 10.31a1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.451 0 1.12 1.12 0 0 0-1.548 0 2.5 2.5 0 0 1-3.452 0 1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.77-3.248l2.889-4.184A2 2 0 0 1 7 2h10a2 2 0 0 1 1.653.873l2.895 4.192a2.5 2.5 0 0 1-3.774 3.244"/>
      <path d="M4 10.95V19a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8.05"/>
    </svg>
  ),
  barChart: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="20" x2="20" y2="20"/>
      <rect x="6" y="13" width="3" height="7" rx="0.5"/>
      <rect x="11" y="9" width="3" height="11" rx="0.5"/>
      <rect x="16" y="5" width="3" height="15" rx="0.5"/>
    </svg>
  ),
  download: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  ),
  box: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <path d="M3.27 6.96 12 12l8.73-5.04"/>
      <path d="M12 22V12"/>
    </svg>
  ),
  cart: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="21" r="1"/>
      <circle cx="20" cy="21" r="1"/>
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 1.99 1.61h9.72a2 2 0 0 0 1.98-1.66L23 6H6"/>
    </svg>
  ),
  tag: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"/>
      <circle cx="7.5" cy="7.5" r="1.2"/>
    </svg>
  ),
  shield: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>
      <path d="m9 12 2 2 4-4"/>
    </svg>
  ),
  move: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="5 9 2 12 5 15"/>
      <polyline points="9 5 12 2 15 5"/>
      <polyline points="15 19 12 22 9 19"/>
      <polyline points="19 9 22 12 19 15"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <line x1="12" y1="2" x2="12" y2="22"/>
    </svg>
  ),
  shuffle: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m18 14 4 4-4 4"/>
      <path d="m18 2 4 4-4 4"/>
      <path d="M2 18h1.973a4 4 0 0 0 3.3-1.7l5.454-8.6a4 4 0 0 1 3.3-1.7H22"/>
      <path d="M2 6h1.972a4 4 0 0 1 3.6 2.2"/>
      <path d="M22 18h-6.041a4 4 0 0 1-3.3-1.8l-.359-.45"/>
    </svg>
  ),
  activity: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2"/>
    </svg>
  ),
  folder: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.9 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/>
    </svg>
  ),
  shirt: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 .55.45 1 1 1h10c.55 0 1-.45 1-1V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23"/>
    </svg>
  ),
  shoppingBag: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 10a4 4 0 0 1-8 0"/>
      <path d="M3.103 6.034h17.794"/>
      <path d="M3.4 5.467a2 2 0 0 0-.4 1.2V20a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.667a2 2 0 0 0-.4-1.2l-2-2.667A2 2 0 0 0 17 2H7a2 2 0 0 0-1.6.8z"/>
    </svg>
  ),
  opportunities: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a7 7 0 00-4 12.7c.6.5 1 1.2 1 2.05V17h6v-.25c0-.85.4-1.55 1-2.05A7 7 0 0012 2z"/>
      <path d="M9 18h6"/>
      <path d="M10 22h4"/>
    </svg>
  ),
  analysis: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="20" x2="20" y2="20"/>
      <rect x="6" y="13" width="3" height="7" rx="0.5"/>
      <rect x="11" y="9" width="3" height="11" rx="0.5"/>
      <rect x="16" y="5" width="3" height="15" rx="0.5"/>
    </svg>
  ),
  products: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 8l-9-5-9 5 9 5 9-5z"/>
      <path d="M3 8v8l9 5 9-5V8"/>
      <path d="M12 13v8"/>
    </svg>
  ),
  bag: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 10a4 4 0 0 1-8 0"/>
      <path d="M3.103 6.034h17.794"/>
      <path d="M3.4 5.467a2 2 0 0 0-.4 1.2V20a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.667a2 2 0 0 0-.4-1.2l-2-2.667A2 2 0 0 0 17 2H7a2 2 0 0 0-1.6.8z"/>
    </svg>
  ),
  default: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/>
      <rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="14" y="14" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/>
    </svg>
  ),
};

const getIconForMenu = (item) => {
  if (item.icon && MENU_ICONS[item.icon]) return MENU_ICONS[item.icon];
  const segment = item.route?.split("/")[1];
  return MENU_ICONS[segment] || MENU_ICONS.default;
};

const flattenSubpages = (pages = []) =>
  pages.flatMap((page) =>
    page.route && !page.pages ? [page] : flattenSubpages(page.pages || [])
);

const getHeaderLabel = (item) => {
  if (item.pages?.length === 1 && item.pages[0]?.pages && item.pages[0]?.pageName) {
    return item.pages[0].pageName;
  }
  return item.menuName;
};

const hasVisibleChildren = (item) => {
  if (Array.isArray(item.sections)) return item.sections.some((section) => section.items?.length > 0);
  return Array.isArray(item.pages) && item.pages.length > 0;
};

const itemHasActiveLeaf = (item, currentPath) => {
  if (Array.isArray(item.sections)) {
    return item.sections.some((section) =>
      section.items.some((leaf) => !leaf.disabled && leaf.route && currentPath === leaf.route)
    );
  }
  if (Array.isArray(item.pages)) {
    return flattenSubpages(item.pages).some(
      (page) => currentPath === `${page.baseRoute ?? item.route}${page.route}`
    );
  }
  return false;
};

export const Sidebar = memo(function Sidebar({ ctx, tenantOverride }) {

  const navigate = useNavigate();
  const location = useLocation();

  const { username, tenant } = useAuth();
  const tenantName = tenantOverride || tenant?.name;

  const { t } = i18n;

  const { userRole, tenantContext } = ctx || {};

  const [expanded, setExpanded] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const mqlRef = useRef(null);

  const initialLetters = username
    ? username.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()
    : "";

  const isActive = (path) => Boolean(path) && location.pathname === path;
  const isActivePrefix = (path) => location.pathname.startsWith(path);

  const navigationOptions = useMemo(
    () => getNavigationOptionsByTenanat(tenantName, t, tenantContext, userRole),
    [tenantName, t, tenantContext, userRole]
  );

  const hasDataIntegration = useMemo(
    () => navigationOptions.some((item) => item.options?.hasDataIntegration),
    [navigationOptions]
  );

  const goToSettings = useCallback(() => {
    navigate(`/configuracoes/${SETTINGS_ROUTES.ACCOUNT.ROUTE}`);
  }, [navigate]);

  const onLogout = useCallback(() => {
    doLogout()
      .catch(() => {})
      .finally(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("exp");
        localStorage.removeItem("store-information");
        localStorage.removeItem("active-store");
        window.location.href = "/";
      });
  }, []);

  useEffect(() => {
    mqlRef.current = window.matchMedia("(min-width: 900px)");
    const mediaQueryChanged = () => setExpanded(false);
    mqlRef.current.addEventListener("change", mediaQueryChanged);
    return () => mqlRef.current.removeEventListener("change", mediaQueryChanged);
  }, []);

  const onSelectTopLevelItem = (item) => {
    if (hasVisibleChildren(item)) {
      setOpenMenu((prev) => (prev === item.menuName ? null : item.menuName));
      return;
    }
    navigate(item.route);
  };

  const onSelectLeaf = (leaf) => {
    if (leaf.disabled || !leaf.route) return;
    navigate(leaf.route);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        height: "100vh",
        background: "#ED701D",
        width: expanded ? 282 : 72,
        transition: "width 0.25s ease",
        overflow: "hidden",
        zIndex: 9999,
        boxShadow: "2px 0 10px rgba(0,0,0,0.15)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => {
        setExpanded(false);
        setOpenMenu(null);
      }}
    >
      <aside
        style={{
          blockSize: "100%", inlineSize: "100%", display: "flex",
          flexDirection: "column", alignItems: "center", justifyContent: "start",
          overflowY: "auto"
        }}>
        <figure
          style={{
            display: "flex", alignItems: "center", gap: "0.7rem",
            justifyContent: expanded ? "flex-start" : "center",
            inlineSize: "100%", paddingInline: expanded ? "1.1rem" : 0,
            paddingBlockStart: "1.4rem", marginBlockEnd: "1.6rem", flexShrink: 0
          }}>
          <img
            style={{ blockSize: "36px", inlineSize: "36px", objectFit: "contain", flexShrink: 0 }}
            src="/img/new/logo-desenho-aidron-branco.png"
            alt="Aidron"
          />
          {expanded && (
            <span style={{ color: "#fff", fontWeight: 700, fontSize: "1.35rem", whiteSpace: "nowrap" }}>
              aidron
            </span>
          )}
        </figure>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.25rem", inlineSize: "100%" }}>
          {navigationOptions.map((item) => {
            const hasChildren = hasVisibleChildren(item);
            const isOpen = openMenu === item.menuName;
            const headerLabel = getHeaderLabel(item);
            const isSelected = hasChildren
              ? (isOpen || itemHasActiveLeaf(item, location.pathname))
              : isActive(item.route);

            return (
              <div key={item.menuName} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.15rem", inlineSize: "100%" }}>
                <MenuItem
                  $selected={isSelected}
                  $expanded={expanded}
                  onClick={() => onSelectTopLevelItem(item)}
                >
                  {getIconForMenu(item)}
                  {expanded && <MenuLabel $expanded={expanded}>{headerLabel}</MenuLabel>}
                  {hasChildren && expanded && (
                    <Chevron $open={isOpen} $visible={expanded}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </Chevron>
                  )}
                </MenuItem>

                {hasChildren && (
                  <FlyoutBody $open={isOpen && expanded}>
                    {Array.isArray(item.sections) ? (
                      item.sections.map((section) => (
                        <div key={section.label}>
                          <SectionLabel>{section.label}</SectionLabel>
                          {section.items.map((leaf) => (
                            <FlyoutItem
                              key={leaf.pageName}
                              $active={!leaf.disabled && isActive(leaf.route)}
                              $disabled={leaf.disabled}
                              onClick={() => onSelectLeaf(leaf)}
                            >
                              {getIconForMenu(leaf)}
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {leaf.pageName}
                              </span>
                            </FlyoutItem>
                          ))}
                        </div>
                      ))
                    ) : (
                      flattenSubpages(item.pages).map((page) => {
                        const fullRoute = `${page.baseRoute ?? item.route}${page.route}`;
                        return (
                          <FlyoutItem
                            key={fullRoute}
                            $active={isActive(fullRoute)}
                            onClick={() => navigate(fullRoute)}>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {page.pageName}
                              </span>
                            {
                              page.isNew && 
                              <span style={{
                                flexShrink: 0,
                                marginLeft: 'auto',
                                backgroundColor: 'rgba(0, 0, 0, 0.45)',
                                color: '#FFD9A0',
                                fontSize: '0.62rem',
                                fontWeight: 700,
                                letterSpacing: '0.02em',
                                padding: '0.15rem 0.4rem',
                                borderRadius: '4px'                                
                              }}/>
                            }
                          </FlyoutItem>
                        );
                      })
                    )}
                  </FlyoutBody>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ flexGrow: 1 }} />

        <div style={{ inlineSize: "calc(100% - 24px)", blockSize: "1.5px", backgroundColor: "rgba(255, 255, 255, 0.35)", marginBlockEnd: "0.5rem", flexShrink: 0 }} />

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.25rem", inlineSize: "100%", flexShrink: 0 }}>

          {/* Alertas ainda não tem rota/funcionalidade própria — exibido apenas visualmente
              para bater com o layout de referência, sem navegação real. */}
          <StaticItem $expanded={expanded}>
            {/* Bell icon */}
            {
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.268 21a2 2 0 0 0 3.464 0"/>
                <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326"/>
              </svg>
            }
            {/* Notificações */}
            <span style={{
              position: "absolute",
              top: '6px',
              right: '10px',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#4CD964',
              border: '1.5px solid #ED701D'
            }}/>

            <span style={{
              position: "absolute",
              top: '6px',
              right: '10px',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#4CD964',
              border: '1.5px solid #ED701D'
            }}/>
            {expanded && <MenuLabel $expanded={expanded}>Alertas</MenuLabel>}
          </StaticItem>

          {hasDataIntegration && (
            <MenuItem
              $selected={isActive(ROUTES_APPLICATION_PATH.DATA_INTEGRATION.BASE_URL)}
              $expanded={expanded}
              onClick={() => navigate(ROUTES_APPLICATION_PATH.DATA_INTEGRATION.BASE_URL)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <ellipse cx="12" cy="5" rx="9" ry="3"/>
                <path d="M3 5V19A9 3 0 0 0 21 19V5"/>
                <path d="M3 12A9 3 0 0 0 21 12"/>
              </svg>
              {expanded && <MenuLabel $expanded={expanded}>Integração de Dados</MenuLabel>}
            </MenuItem>
          )}

          <MenuItem $active={isActivePrefix("/configuracoes")} $expanded={expanded} onClick={goToSettings}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12.22 2h-.44a2 2 0 00-2 2v.18a2 2 0 01-1 1.73l-.43.25a2 2 0 01-2 0l-.15-.08a2 2 0 00-2.73.73l-.22.38a2 2 0 00.73 2.73l.15.1a2 2 0 011 1.72v.51a2 2 0 01-1 1.74l-.15.09a2 2 0 00-.73 2.73l.22.38a2 2 0 002.73.73l.15-.08a2 2 0 012 0l.43.25a2 2 0 011 1.73V20a2 2 0 002 2h.44a2 2 0 002-2v-.18a2 2 0 011-1.73l.43-.25a2 2 0 012 0l.15.08a2 2 0 002.73-.73l.22-.39a2 2 0 00-.73-2.73l-.15-.08a2 2 0 01-1-1.74v-.5a2 2 0 011-1.74l.15-.09a2 2 0 00.73-2.73l-.22-.38a2 2 0 00-2.73-.73l-.15.08a2 2 0 01-2 0l-.43-.25a2 2 0 01-1-1.73V4a2 2 0 00-2-2z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
            {expanded && <MenuLabel $expanded={expanded}>Configurações</MenuLabel>}
          </MenuItem>
        </div>

        <div style={{ marginBlockEnd: "0.5rem", flexShrink: 0 }} />
        <div style={{ inlineSize: "calc(100% - 24px)", blockSize: "1.5px", backgroundColor: "rgba(255, 255, 255, 0.35)", flexShrink: 0 }} />
        <div style={{ marginBlockEnd: "0.5rem", flexShrink: 0 }} />

        <UserSection $expanded={expanded} style={{ flexShrink: 0 }}>
          <p
            style={{
              inlineSize: "36px", blockSize: "36px", borderRadius: "50%", backgroundColor: "rgba(255, 255, 255, 0.25)",
              color: "#fff", fontWeight: 700, fontSize: "0.8rem", display: "flex", flexDirection: "row",
              alignItems: "center", justifyContent: "center"
            }}>
            {initialLetters}
          </p>
          <div>
            <strong>{username}</strong>
            <p>{tenantName}</p>
          </div>
        </UserSection>

        <MenuItem $expanded={expanded} onClick={onLogout} style={{ marginBlockEnd: "0.5rem", flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          {expanded && <MenuLabel $expanded={expanded}>Sair</MenuLabel>}
        </MenuItem>
      </aside>
    </div>
  );
});